import { useEffect, useState } from "react";
import styles from "./Settings.module.scss";
import { SettingsService } from "../../services/settings.service";
import { settingsDataType } from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import * as Yup from "yup";
import { Empty, Modal, Popconfirm, message } from "antd";
import { useFormik } from "formik";
import ErrorMessage from "../../components/shared/ErrorMessage/ErrorMessage";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { FaPlus } from "react-icons/fa";
const Settings = () => {
  const [settings, setSettings] = useState<settingsDataType[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  useEffect(() => {
    setLoading(true);
    SettingsService.getAllSettings()
      .then((response) => {
        if (Array.isArray(response?.data)) {
          const dataWithOriginalValues = response?.data?.map((item) => ({
            ...item,
            originalValue: item.value,
          }));
          setSettings(dataWithOriginalValues);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleInputChange = (id: number, key: string, newValue: string) => {
    setSettings((prevSettings) =>
      prevSettings.map((item) =>
        item.id === id ? { ...item, key: key, value: newValue } : item
      )
    );
  };

  const { handleSubmit, handleChange, values, resetForm, touched, errors } =
    useFormik({
      initialValues: {
        key: "",
        value: "",
      },
      validationSchema: Yup.object({
        key: Yup.string().required("Parametrin adı tələb olunur."),
        value: Yup.string().required("Parametrin dəyəri tələb olunur."),
      }),
      onSubmit: async (values) => {
        const response = await SettingsService.addSettings(values);
        if (response?.data) {
          setLoading(true);
          setSettings((prevSettings) => {
            const updatedSettings = [...prevSettings, response?.data?.data];
            resetForm();
            setIsModalVisible(false);
            return updatedSettings;
          });
          message.success("Parametr uğurla əlavə edildi!");
        } else {
          message.error(response.errorMessage);
        }

        setLoading(false);
      },
    });

  const handleAddClick = () => {
    setIsModalVisible(true);
  };

  const handleSave = async () => {
    const updatedSettings = settings.filter(
      (item) => item.value !== item.originalValue
    );

    if (updatedSettings.length === 0) {
      message.info("Yadda saxlamaq üçün dəyişiklik yoxdur.");
      return;
    }

    const data = updatedSettings.map((item) => ({
      key: item.key,
      value: item.value,
    }));

    if (!data.length) {
      message.error("Yadda saxlamaq üçün etibarlı dəyişiklik yoxdur.");
      return;
    }
    const response = await SettingsService.updateSettings(data);
    if (response.status === 200) {
      message.success("Bütün dəyişikliklər uğurla yadda saxlanıldı!");
      setSettings((prevSettings) =>
        prevSettings.map((item) => ({
          ...item,
          originalValue: item.value,
        }))
      );
    } else {
      message.error("Failed to save changes. Please try again.");
    }
  };

  const handleDelete = async (id: any) => {
    await SettingsService.deleteSettings(id);
    setSettings((prevSettings) =>
      prevSettings.filter((item) => item.id !== id)
    );
    message.success("Parametr uğurla silindi!");
  };

  return (
    <div id="pages">
      <Helmet>
        <title>BSC | Parametrlər</title>
      </Helmet>
      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Parametrlər</h3>
          </div>
          <div>
            <div className="pages_components">
              <MainButton onClick={handleAddClick}>
                <FaPlus size={20} />
              </MainButton>
              <div className={styles.settings_form}>
                {settings.length > 0 ? (
                  settings.map((item) => (
                    <div key={item.id} className={styles.settings_card}>
                      <p className={styles.label}>
                        <label htmlFor={`key-${item.id}`}>{item.key}</label>
                      </p>
                      <div className={styles.input_wrapper}>
                        <input
                          type="text"
                          id={`key-${item.id}`}
                          value={item.value}
                          onChange={(e) =>
                            handleInputChange(item.id, item.key, e.target.value)
                          }
                        />
                        <span className={styles.focus_border}></span>
                        <Popconfirm
                          title="Bu parametri silmək istədiyinizə əminsiniz?"
                          onConfirm={() => handleDelete(item.id)}
                          onCancel={() =>
                            message.info("Silinmə əməliyyatı ləğv edildi.")
                          }
                          okText="Bəli"
                          cancelText="Xeyr"
                          placement="topRight"
                        >
                          <div className={styles.delete_icon}>
                            <i className="fa-solid fa-trash"></i>
                          </div>
                        </Popconfirm>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.no_data}>
                    <div className={styles.no_data_icon}>
                      <Empty description="No data" />
                    </div>
                  </div>
                )}
              </div>
              {settings.length > 0 && (
                <MainButton onClick={handleSave}>
                  Dəyişiklikləri yadda saxlayın
                </MainButton>
              )}
            </div>
          </div>
        </div>
      )}
      <Modal
        title={"Yeni Parametr əlavə edin"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        style={{ minWidth: "340px" }}
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="form_input">
            <label className="label" htmlFor="key">
              Parametrin adı
            </label>
            <div className="input_wrapper">
              <input
                type="text"
                id="key"
                name="key"
                placeholder="Parametrin adı"
                value={values.key}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.key} touched={touched.key} />
          </div>
          <div className="form_input">
            <label className="label" htmlFor="value">
              Parametrin dəyəri
            </label>
            <div className="input_wrapper">
              <input
                type="text"
                id="value"
                name="value"
                placeholder="Parametrin dəyəri"
                value={values.value}
                onChange={handleChange}
              />
              <span className="focus_border"></span>
            </div>
            <ErrorMessage error={errors.value} touched={touched.value} />
          </div>
          <MainButton type="submit" full>
            Əlavə edin
          </MainButton>
        </form>
      </Modal>
    </div>
  );
};

export default Settings;
